import { useForm } from "@refinedev/antd";
import { Form, Input } from "antd";
import {
  HttpError,
  useCustomMutation,
  useGo,
  useLogin,
  useTranslate,
} from "@refinedev/core";
import styled from "styled-components";
import { useCallback, useState } from "react";
import { AnalyticsEvents } from "types/analytics/";
import { useAnalytics } from "hooks/useAnalytics/";
import { LoadingSpinner } from "components/LoadingSpinner/";
import {
  AuthSubmitButton,
  AuthLink,
  AuthSecurityNote,
  AuthBottomLine,
} from "./AuthFormElements";
import { LockIcon } from "@phosphor-icons/react";

type FormData = {
  code: string;
  email?: string;
};

export type VerificationFormProps = {
  email: string;
  password?: string;
  fromLogin?: boolean;
};

const isEmpty = (value: string) => /^\s*$/.test(value);

export const VerificationForm = ({
  email,
  password,
  fromLogin,
}: VerificationFormProps) => {
  const t = useTranslate();
  const { mutate: resendCode, isPending: isResending } = useCustomMutation();
  const [code, setCode] = useState("");
  const [isOtpComplete, setIsOtpComplete] = useState(false);

  const go = useGo();
  const { mutate: login } = useLogin();
  const { trackOnboardingEvent, trackEvent } = useAnalytics();
  const { onFinish, formProps, formLoading, form } = useForm<
    FormData,
    HttpError,
    FormData
  >({
    action: "create",
    redirect: false,
    resource: `api/users/creation_validation`,
    successNotification: false,
    onMutationSuccess: () => {
      trackOnboardingEvent(
        AnalyticsEvents.EmailVerified,
        {},
        undefined,
        "email"
      );
      trackEvent(AnalyticsEvents.UserActivated, {
        signup_method: "email",
      });
      if (password) {
        login({ email, password });
      } else {
        go({ to: "/login?email=" + btoa(email) });
      }
    },
  });

  const handleVerify = useCallback(() => {
    if (code.length === 6) {
      void (() => onFinish({ code, email }))();
    }
  }, [code, email, onFinish]);

  const handleFinish = (values: FormData) => {
    void (() => onFinish({ ...values, email }))();
  };

  const handleResendCode = () => {
    resendCode({
      url: "api/users/resend_code",
      method: "post",
      values: { email },
      successNotification: {
        message: t("pages.auth.verification.verificationCodeResent"),
        description: t("pages.auth.verification.pleaseCheckYour"),
        type: "success",
      },
    });
  };

  const isButtonLoading = formLoading;
  const isButtonDisabled = formLoading || !isOtpComplete || isResending;
  const verifyButtonText = isButtonLoading
    ? t("pages.auth.verification.verifying")
    : t("pages.auth.verification.verify");

  return (
    <>
      <Subtitle>
        {fromLogin
          ? t("pages.auth.verification.verifyToLogin")
          : t("pages.auth.verification.enterCodeSentTo")}
      </Subtitle>
      <EmailDisplay>{email}</EmailDisplay>

      <LoadingSpinner loading={formLoading} />
      <Form
        layout="vertical"
        {...formProps}
        onFinish={handleFinish}
        onChange={() => {
          const currentCode = form.getFieldValue("code") as string;
          if (isEmpty(currentCode)) {
            setIsOtpComplete(false);
          }
        }}
        style={{ width: "100%" }}
      >
        <Form.Item name="code" style={{ marginBottom: 12 }}>
          <OTPWrapper>
            <Input.OTP
              data-testid="verification-code"
              size="large"
              onChange={(value) => {
                if (!formLoading && value.length === 6 && !isEmpty(value)) {
                  setCode(value);
                  setIsOtpComplete(true);
                  void (() => onFinish({ code: value, email }))();
                }
              }}
            />
          </OTPWrapper>
        </Form.Item>

        <ResendSection>
          <AuthBottomLine>
            {t("pages.auth.verification.didntGetIt")}{" "}
          </AuthBottomLine>
          <AuthLink onClick={handleResendCode}>
            {t("pages.auth.verification.resendCode")}
          </AuthLink>
        </ResendSection>

        <AuthSubmitButton
          onClick={handleVerify}
          disabled={isButtonDisabled}
          type="button"
        >
          {verifyButtonText}
        </AuthSubmitButton>
      </Form>

      <AuthLink
        onClick={() => go({ to: "/login" })}
        style={{
          display: "block",
          textAlign: "center",
          marginTop: 16,
          fontSize: 14,
        }}
      >
        ← {t("pages.auth.verification.backToLogin")}
      </AuthLink>

      <AuthSecurityNote>
        <LockIcon size={12} />
        {t("pages.auth.new-register.trustBadge")}
      </AuthSecurityNote>
    </>
  );
};

const Subtitle = styled.p`
  font-size: 14px;
  color: #374151;
  text-align: center;
  margin: 0;
`;

const EmailDisplay = styled.div`
  font-size: 14px;
  font-weight: 500;
  text-align: center;
  color: #111827;
`;

const OTPWrapper = styled.div`
  .ant-otp {
    width: 100%;
    display: flex;
    justify-content: space-between;
    gap: 8px;
  }

  .ant-otp-input {
    flex: 1;
    height: 56px;
    font-size: 24px;
    font-weight: 600;
    font-family: "Inter Variable", sans-serif;
    border-radius: 8px;
    border-color: #e5e7eb;
    display: flex;
    align-items: center;
    justify-content: center;
    transition:
      border-color 0.15s,
      box-shadow 0.15s;

    &:focus {
      border-color: #14b8a6;
      box-shadow: 0 0 0 3px rgba(20, 184, 166, 0.1);
    }
  }
`;

const ResendSection = styled.div`
  text-align: center;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-family: "Inter Variable", sans-serif;
`;
